import Cookie from 'js-cookie';
import { Dictionary } from './Dictionary';
/**
 * 存储项
 * 目的 将对象序列化和反序列化
 * @param key 存储的值
 * @param value 存储的值/对象 或 已被存储的字符串
 */
export class StorageItem<T> {
  key: string;
  value: T;
  expires?: number;
  domain?: string;
  constructor(key: string, value: T, expires?: number, domain?: string) {
    this.key = key;
    this.value = value;
    this.expires = expires;
    this.domain = domain;
  }

  /**
   * 序列化具体实现
   * @param obj 要序列化的对象
   * @returns 序列化以后的对象
   */
  private replacer(obj: any) {
    if (typeof obj === 'object' && obj !== null) {
      const copyObj: any = Object.prototype.toString.call(obj) === '[object Array]' ? [] : {};
      for (const key in obj) {
        const value = obj[key];
        // 如果是Date类型
        if (Object.prototype.toString.call(value) === '[object Date]') {
          copyObj[key] = `__Type__Date__${value.getTime()}`;
        } else {
          copyObj[key] = this.replacer(value);
        }
      }
      return copyObj;
    }
    if (obj === null) {
      return obj;
    } else {
      return obj;
    }
  }

  /**
   * 反序列化具体实现
   * @param key 健
   * @param value 值
   * @returns 反序列化后的数据
   */
  private reviver(key: string, value: any) {
    if (key === '') {
      return value;
    }
    // 如果是Date类型 将字符串转成Date
    if (/^(__Type__Date__)/.test(value)) {
      return new Date(Number(value.replace('__Type__Date__', '')));
    } else {
      return value;
    }
  }

  /**
   * @returns string 将存储项的value序列化
   */
  serialize(): string {
    return JSON.stringify(this.value);
  }

  /**
   * @returns T 将存储项的value反序列化
   */
  deserialize(): T {
    return JSON.parse(this.value as unknown as string);
  }

  /**
   * @returns 存储项 被存储时占用的空间
   */
  size(): number {
    return this.key.length + this.serialize().length;
  }
}

/**
 * 存储底层的抽象基类  所有的抽象底层都需要继承该类
 */
abstract class BaseStorage {
  // 总存储
  size: number;
  // 已用存储
  use: number;
  // 存储了哪些key
  keys: Array<string>;

  constructor() {
    this.size = 1024;
    this.use = 0;
    this.keys = [];
  }

  abstract get(key: string): StorageItem<unknown>;
  abstract set(storageItem: StorageItem<unknown>): boolean;
  abstract remove(key: string): void;
  abstract includes(key: string): boolean;
  abstract clear(): void;

  /**
   * 当前存储底层的剩余空间
   */
  abstract extraSpace(): number

  /**
   * 监听其他页签存储是否有变化
   */
  abstract listen(): void

  /** 生成随机数 时间戳后6位+4位随机字母  */
  genRandomKey(): string {
    const datetime: number = new Date().getTime();
    const chars: Array<string> = [];
    for (let i = 0; i < 4; i++) {
      const randomNum = 65 + Math.floor(Math.random() * 26);
      chars.push(String.fromCharCode(randomNum));
    }
    const randomKey = (datetime + chars.join('')).slice(-10);
    return randomKey;
  }
}

/**
 * 根据过期时间存储
 */
class CoookieStorage extends BaseStorage {
  // 每次更新cookie会重新设置storageKey的值,通过LocalStorage监听storageKey的值,来动态更新keys(可以跨页签)
  static storageKey = 'cookie_storage_version';
  // 域名及子域名
  domains: Array<string>;
  // 存储key的上限 Opera每个域名cookie限制为30个 其余均为50 暂时用20个 留下10个给httpOnly
  keySizeLimit = 20;

  constructor() {
    super();
    this.size = 1024 * 3; // 最大4K  用3K 留下1K给 httpOnly的
    const domains: Array<string> = [];
    const domainSlice = document.domain.split('.');
    domainSlice.reduceRight((preValue, curValue) => {
      const domain = `${curValue}.${preValue}`;
      domains.push(domain);
      return domain;
    });
    this.domains = domains;
    this.calc();
    this.listen();
  }

  get(key: string): StorageItem<unknown> {
    const val = Cookie.get(key);
    // console.log('cookie get', val);
    // 过期了 或人为删除
    if (val === undefined) {
      this.calc();
      return new StorageItem(key, null);
    }
    return new StorageItem(key, val);
  }

  set(storageItem: StorageItem<unknown>): boolean {
    // Cookie的大小是含等号的 所以 + 1
    const storageItemSize = storageItem.size() + 1;
    if (this.extraSpace() >= storageItemSize && this.keySizeLimit > this.keys.length) {
      // console.log(`${storageItem.key}已被存储`);
      this.keys.push(storageItem.key);
      this.use += storageItemSize;
      // localStorage.setItem(storageItem.key, storageItem.serialize())
      const options: any = {};
      if (storageItem.expires) {
        options['.expires'] = String(storageItem.expires / 24 / 60 / 60);
      }
      if (storageItem.domain) {
        options['.domain'] = storageItem.domain;
      }
      Cookie.set(storageItem.key, storageItem.serialize(), options);
      this.notify();
      return true;
    }
    console.warn(`${storageItem.key}${storageItemSize}的占用容量大于PersistentStorage剩余容量${this.extraSpace()}, 存储失败`);
    return false;
  }

  remove(key: string): void {
    // console.log('persistentStorage keys after', this.keys);
    const keyIndex = this.keys.indexOf(key);
    if (keyIndex !== -1) {
      this.use -= (key + '=' + Cookie.get(key)).length;
      this.keys.splice(keyIndex, 1);
    }
    Cookie.remove(key);
    // console.log('this.domains', this.domains);
    this.domains.forEach((domain: string) => {
      // console.log('this.domains', this.domains);
      Cookie.remove(key, { domain });
    });
    this.notify();
    // console.log('persistentStorage keys after', this.keys);
  }

  clear(): void {
    this.keys.forEach((key: string) => {
      this.domains.forEach((domain: string) => {
        Cookie.remove(key, { domain });
      });
    });
    this.notify();
  }

  includes(key: string): boolean {
    return this.keys.includes(key);
  }

  extraSpace(): number {
    return this.size - this.use;
  }

  listen(): void {
    if (window.addEventListener) {
      window.addEventListener('storage', (e) => {
        if (e.key === CoookieStorage.storageKey) {
          this.calc();
        }
      }, false);
    }
  }

  private notify(): void {
    const item = new StorageItem(CoookieStorage.storageKey, this.genRandomKey());
    localStorage.setItem(item.key, item.serialize());
  }

  /** 计算use和keys */
  calc(): void {
    let use = 0;
    const keys: string[] = [];
    const cookieStrArr = document.cookie.split('; ');
    use = document.cookie.split('; ').join('').length;
    cookieStrArr.forEach(item => {
      keys.push(item.replace(/=.*/g, ''));
    });
    this.use = use;
    this.keys = keys;
    // console.log(`Cookie 计算后的use: ${use} keys: ${keys}`);
  }
}

class PersistentStorage extends BaseStorage {
  constructor() {
    super();
    this.size = 1024 * 1024 * 5; // 最大5M
    this.calc();
    this.listen();
  }

  get(key: string): StorageItem<unknown> {
    const val = localStorage.getItem(key);
    return new StorageItem(key, val);
  }

  set(storageItem: StorageItem<unknown>): boolean {
    const storageItemSize = storageItem.size();
    if (this.extraSpace() >= storageItemSize) {
      // console.log(`${storageItem.key}已被存储`);
      this.keys.push(storageItem.key);
      this.use += storageItemSize;
      localStorage.setItem(storageItem.key, storageItem.serialize());
      return true;
    }
    console.warn(`${storageItem.key}${storageItem.size()}的占用容量大于PersistentStorage剩余容量${this.extraSpace()}, 存储失败`);
    return false;
  }

  remove(key: string): void {
    // console.log('persistentStorage remove before keys', this.keys);
    const keyIndex = this.keys.indexOf(key);
    if (keyIndex !== -1) {
      this.use -= (key + '' + window.localStorage.getItem(key)).length;
      this.keys.splice(keyIndex, 1);
    }
    localStorage.removeItem(key);
    // console.log('persistentStorage remove after  keys', this.keys);
  }

  clear(): void {
    this.keys = [];
    this.use = 0;
    localStorage.clear();
  }

  includes(key: string): boolean {
    return this.keys.includes(key);
  }

  extraSpace(): number {
    return this.size - this.use;
  }

  listen(): void {
    if (window?.addEventListener) {
      window.addEventListener('storage', (e) => {
        if (e.storageArea === localStorage) {
          this.calc();
        }
      }, false);
    }
  }

  calc(): void {
    let use = 0;
    const keys: string[] = [];
    for (const item in window.localStorage) {
      if (Object.prototype.hasOwnProperty.call(window.localStorage, item)) {
        keys.push(item);
        use += (item + '' + window.localStorage.getItem(item)).length;
      }
    }
    this.use = use;
    this.keys = keys;
    // console.log(`Persistent 计算后的use: ${use} keys: ${keys}`);
  }
}

class SessionStorage extends BaseStorage {
  static storageKey = 'session_storage_version';

  constructor() {
    super();
    this.size = 1024 * 1024 * 5; // 最大5M
    this.calc();
    this.listen();
  }

  get(key: string): StorageItem<unknown> {
    const val = sessionStorage.getItem(key);
    return new StorageItem(key, val);
  }

  set(storageItem: StorageItem<unknown>): boolean {
    const storageItemSize = storageItem.size();
    if (this.extraSpace() >= storageItemSize) {
      // console.log(`${storageItem.key}已被存储`);
      this.keys.push(storageItem.key);
      this.use += storageItemSize;
      sessionStorage.setItem(storageItem.key, storageItem.serialize());
      this.notify();
      return true;
    }
    console.warn(`${storageItem.key}${storageItem.size()}的占用容量大于SessionStorage剩余容量${this.extraSpace()}, 存储失败`);
    return false;
  }

  remove(key: string): void {
    // console.log('SessionStorage keys before', this.keys);
    const keyIndex = this.keys.indexOf(key);
    if (keyIndex !== -1) {
      this.use -= (key + '' + window.sessionStorage.getItem(key)).length;
      this.keys.splice(keyIndex, 1);
    }
    sessionStorage.removeItem(key);
    this.notify();
    // console.log('SessionStorage keys after', this.keys);
  }

  clear(): void {
    this.keys = [];
    this.use = 0;
    sessionStorage.clear();
    this.notify();
  }

  includes(key: string): boolean {
    return this.keys.includes(key);
  }

  extraSpace(): number {
    return this.size - this.use;
  }

  notify(): void {
    const item = new StorageItem(SessionStorage.storageKey, this.genRandomKey());
    localStorage.setItem(item.key, item.serialize());
  }

  listen(): void {
    if (window.addEventListener) {
      window.addEventListener('storage', (e) => {
        // console.log('SessionStorage.storageKey', e);
        if (e.key === SessionStorage.storageKey) {
          this.calc();
        }
      }, false);
    }
  }

  calc(): void {
    let use = 0;
    const keys: string[] = [];
    for (const item in window.sessionStorage) {
      if (Object.prototype.hasOwnProperty.call(window.sessionStorage, item)) {
        keys.push(item);
        use += (item + '' + window.sessionStorage.getItem(item)).length;
      }
    }
    this.use = use;
    this.keys = keys;
    // console.log(`Session 计算后的use: ${use} keys: ${keys}`);
  }
}

/** 存储类型枚举 */
export enum EnumStorageType {
  /** 持久存储 */
  Persistent = 1,
  /** 会话存储 */
  Session = 2,
  /** 过期时间存储 */
  Expiration = 4
}

/**
 * 存储库：整合了多种前端存储机制
 */
export class StorageLib {
  private storageList: Dictionary<EnumStorageType, BaseStorage>;
  private static _instance: StorageLib = new StorageLib();

  /**
   * 获取存储的唯一实例
   */
  public static get instance(): StorageLib {
    return this._instance;
  }

  /**
   * 私有构造函数
   * 三种类型的存储各自建立一个示例
   */
  private constructor() {
    this.storageList = new Dictionary<EnumStorageType, BaseStorage>();
    this.storageList.append(EnumStorageType.Persistent, new PersistentStorage());
    this.storageList.append(EnumStorageType.Session, new SessionStorage());
    this.storageList.append(EnumStorageType.Expiration, new CoookieStorage());
  }

  /**
   * 根据参数key从存储中获取存储的数据 如果出入泛型T 则返回值直接会转成T类型
   * @param storageType 存储类型
   * @param key 键
   * @returns 换取的对象
   */
  public getItem<T>(storageType: EnumStorageType, key: string): T {
    const storage = this.storageList.getValue(storageType);
    if (storage.includes(key)) {
      const storageItem: StorageItem<unknown> = storage.get(key);
      return storageItem.deserialize() as T;
    }
    return null;
  }

  /**
   * 存储的封装 内部调用 外部不可见
   * @param storageType 存储类型
   * @param key 键
   * @param value 值
   * @param second 失效时间，秒
   * @param domain 域名，仅对过期类型有效
   */
  public setItem(storageType: EnumStorageType, key: string, value: unknown, second?: number, domain?: string): void {
    const storage = this.storageList.getValue(storageType);
    const storageItem = new StorageItem(key, value, second, domain);
    storage.set(storageItem);
  }

  /**
   * 根据key删除存储的数据
   * @param storageType 存储类型
   * @param key 键
   */
  public removeItem(storageType: EnumStorageType, key: string): void {
    const storage = this.storageList.getValue(storageType);
    storage.remove(key);
  }

  /**
   * 清空所有存储的数据
   */
  public clearAll(): void {
    this.storageList.Entries.forEach(element => {
      element.value.clear();
    });
  }

  /**
   * 清空指定存储类型的数据
   * @param storageType 存储类型
   */
  public clear(storageType: EnumStorageType) {
    const storage = this.storageList.getValue(storageType);
    storage.clear();
  }
}

// /**
//  * 自测用例 基本全覆盖
//  */
// class User {
//   constructor(age: number, isMan: boolean, birth: Date) {
//     this.age = age;
//     this.isMan = isMan;
//     this.birth = birth;
//   }

//   age: number;
//   isMan: boolean;
//   birth: Date;
//   sliblings: Array<User>;
// }
// const user = new User(2, true, null);
// const userSliblings = new User(4, true, new Date());
// user.sliblings = [userSliblings];

// StorageLib.instance.setItem(EnumStorageType.Session, 'user', user);
// const storageUser = StorageLib.instance.getItem<User>(EnumStorageType.Session, 'user');
// // console.log(storageUser, 'sss', storageUser.sliblings);
// StorageLib.instance.removeItem(EnumStorageType.Session, 'user');

// StorageLib.instance.setItem(EnumStorageType.Expiration, 'user', user, 5);
// setTimeout(() => {
//   const cookieUser = StorageLib.instance.getItem<User>(EnumStorageType.Expiration, 'user');
//   // console.log('cookieUser', cookieUser);
// }, 7000);

// StorageLib.instance.setItem(EnumStorageType.Expiration, 'cookuser2', user, 1000, 'metapattern.com');
// const cookieUser = StorageLib.instance.getItem<User>(EnumStorageType.Expiration, 'cookuser2');
// // console.log('cookieUser2', cookieUser);
// setTimeout(() => {
//   // StorageLib.removeItem('cookuser')
//   StorageLib.instance.clearAll();
//   const cookieUser = StorageLib.instance.getItem<User>(EnumStorageType.Expiration, 'cookuser2');
//   // console.log('cookieUser2', cookieUser);
// }, 12000);

// StorageLib.instance.setItem(EnumStorageType.Expiration, 'cookuser3', new User(33, false, new Date()), 0, 'metapattern.com');
// const cookieUser3 = StorageLib.instance.getItem<User>(EnumStorageType.Expiration, 'cookuser3');
// // console.log('cookieUser3', cookieUser3);
// setTimeout(() => {
//   // StorageLib.removeItem('cookuser')
//   const cookieUser3 = StorageLib.instance.getItem<User>(EnumStorageType.Expiration, 'cookuser3');
//   // console.log('cookieUser3', cookieUser3);
// }, 12000);

// StorageLib.instance.setItem(EnumStorageType.Persistent, 'user2', JSON.stringify(user));
// const user2 = JSON.parse(StorageLib.instance.getItem(EnumStorageType.Persistent, 'user2'));
// // console.log(user2.sliblings);
// StorageLib.instance.removeItem(EnumStorageType.Persistent, 'user2');
// StorageLib.instance.setItem(EnumStorageType.Persistent, 'item', new Array(5242091).fill(6).join(''));
//
